/**
 * The editor's right-hand pane: the settings tabs for the selected snapshot.
 *
 * The panels themselves are the version editor's, unchanged. They only ever
 * talk to the engine over the postMessage bridge and hand their results back
 * through `setSelectedVersionPathValue`, so all they need is something that
 * looks like a version and a callback that writes. Both are built here — see
 * `versionAdapter` below — and the write lands in the snapshot file instead of
 * a mongo document.
 *
 * The chrome deliberately matches the version editor's playEngine panel — the
 * same vertical icon rail on the same surface colour — so moving between the two
 * editors does not feel like moving between two products.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { shouldRebuildPanel } from "../../modules/panelFreeze";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { IconButton, Tooltip, Typography } from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import TuneIcon from "@material-ui/icons/Tune";
import AssignmentIcon from "@material-ui/icons/Assignment";
import PaletteIcon from "@material-ui/icons/Palette";
import PipelineIcon from "@material-ui/icons/Build";
import ListAltIcon from "@material-ui/icons/ListAlt";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import CodeIcon from "@material-ui/icons/Code";
import WidgetsIcon from "@material-ui/icons/Widgets";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";
import LockIcon from "@material-ui/icons/Lock";
import SnapshotAgent from "../containerSnapshotAgent";
import JobQueuePanel from "./containerJobQueue";
import { hasAdsManagerClaims } from "../../modules/snapshotEditorApi";
import ContainerAssets from "./containerAssets";
import ContainerSnapshotToolbox from "./containerSnapshotToolbox";
import PlayEngineVersionSettings from "../containerPlayEngineVersionSettings";
import PlayEngineGeneralSettings from "../containerPlayEngineGeneralSettings";
import PlayEngineScripts from "../containerPlayEngineScripts";
import PlayEngineCustomVariables from "../containerPlayEngineCustomVariables";
import PlayEngineDesign from "../playEngineDesign/containerPlayEngineDesign";
import PlayEngineSounds from "../containerPlayEngineSounds";
import { localFileToBase64 } from "../../modules/playEngineAssetUtils";
import { SNAPSHOT_IFRAME_ID, guessSnapshotAssetsFolder, sourceFromLibraryEntry } from "../../modules/snapshotEditorApi";
import DialogAssetLibrary, { LIBRARY_ASSET_TYPES } from "./dialogAssetLibrary";
import { keepUploadInLibrary } from "../../modules/snapshotCustomUploads";
import { optimizeSourceForSnapshot } from "../../modules/snapshotOptimize";
// Same palette as containerPlayEngineVersionSettings, so every tab in this pane
// shares one surface with the version editor's.
import { PANEL_BG, SECTION_DIVIDER } from "./panelPalette";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "435px",
    minWidth: "500px",
  },
  body: { display: "flex", flex: 1, minHeight: 0, backgroundColor: PANEL_BG },
  rail: {
    display: "flex",
    flexDirection: "column",
    width: 44,
    flexShrink: 0,
    overflow: "hidden",
    borderRight: `1px solid ${SECTION_DIVIDER}`,
  },
  railFooter: {
    borderTop: `1px solid ${SECTION_DIVIDER}`,
    paddingTop: 2,
    paddingBottom: 2,
  },
  // Sticky: the panel below scrolls, and the reason its controls are greyed out
  // should not scroll away with them.
  lockedBanner: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 10px",
    fontSize: "12px",
    color: "#c8a45c",
    backgroundColor: "#3b352a",
    borderBottom: `1px solid ${SECTION_DIVIDER}`,
  },
  // Greyed, desaturated and inert. `pointer-events` sits on the CHILDREN, not
  // on the panel itself: a panel that is its own scroll container would stop
  // receiving wheel events and its content would become unreachable.
  lockedPanel: {
    opacity: 0.55,
    filter: "grayscale(0.35)",
    "& > *": { pointerEvents: "none" },
  },
  // Version Settings is the exception. Its top half is the component outliner —
  // navigation, not editing — so it stays lit and clickable: browsing a locked
  // snapshot is the point of having one. Only the properties panel below the
  // divider is frozen. The outliner's own edit buttons need no help here; they
  // already carry `disabled={userPermissions.editContent === false}`.
  lockedPropsOnly: {
    "& .proto-props-panel": {
      opacity: 0.55,
      filter: "grayscale(0.35)",
    },
    "& .proto-props-panel > *": { pointerEvents: "none" },
  },
  panel: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    "&::-webkit-scrollbar": { display: "none" },
  },
  placeholder: { padding: "16px", opacity: 0.7, fontSize: "13px" },
}));

// The generic playEngine "Agent" tab is deliberately absent: this base is served
// by the snapshot editor pipeline, which has its own tab at the end.
const BASE_TABS = [
  { key: "versionSettings", label: "Version Settings", icon: <SettingsIcon /> },
  { key: "assets", label: "Assets", icon: <PhotoLibraryIcon /> },
  { key: "generalSettings", label: "General Settings", icon: <TuneIcon /> },
  { key: "customVariables", label: "Custom Variables", icon: <ListAltIcon /> },
  // The authored view of the same globalVariables, typed by a design descriptor
  // stored beside the snapshots. Sits next to Custom Variables rather than
  // replacing it: a value the design does not mention stays reachable there.
  { key: "design", label: "Design", icon: <PaletteIcon /> },
  // ENGINE sounds — the global slots a snapshot declares. A component's own
  // "audio" dependency is a different thing and stays in Version Settings.
  { key: "sounds", label: "Sounds", icon: <VolumeUpIcon /> },
  // The snapshot's embedded gameplay layer (`scripts[]`). Same panel the version
  // editor uses: it only needs a version-shaped object and the single write
  // path, both of which the adapter below already provides.
  { key: "scripts", label: "Scripts", icon: <CodeIcon /> },
  // Scriptable buttons from the flow's snapshots/toolboxScripts folder, bound
  // to the snapshot like a design. Runs in a worker; see the component.
  { key: "toolbox", label: "Toolbox", icon: <WidgetsIcon /> },
  { key: "snapshotAgent", label: "Snapshot Pipeline", icon: <PipelineIcon /> },
];

/**
 * How the settings panel gets at assets here: it does not. A snapshot carries
 * its assets inline, and the export path reads the file directly and cannot
 * resolve an "@server:" reference into the media library — so an added asset is
 * embedded as base64 and nothing is stored anywhere else.
 */
// `hasLibrary: false` means the mongo media library — snapshots embed their
// bytes instead of referencing records. The scraped asset library is a separate
// thing, offered through `pickFromLibrary` below, which is built per-mount
// because it has to open a dialog.
/**
 * Uploads from Version Settings.
 *
 * `hasLibrary: false` refers to the mongo media library — snapshots embed their
 * bytes rather than referencing records. The scraped asset library is a separate
 * thing, reached through `pickFromLibrary`, which is built per-mount because it
 * opens a dialog.
 *
 * `baseID` and `createSnackbar` are bound in so an upload can also be kept in
 * that library when the preference is on.
 */
function buildUploadProvider({ baseID, createSnackbar }) {
  const optimize = (assetType, source, fileName) =>
    optimizeSourceForSnapshot({
      baseID,
      assetType,
      source,
      fileName,
      onSaved: (stats) =>
        createSnackbar?.(`Image optimized by ${stats.savedPercent}% for this snapshot.`, "success", false),
    });

  return {
    hasLibrary: false,
    canDeleteFromLibrary: false,
    async uploadAsset(file, depType) {
      const base64 = await localFileToBase64(file);
      // The library keeps the file as picked; only what the snapshot embeds is
      // compressed, so this is deliberately the uncompressed `base64`.
      void keepUploadInLibrary({
        baseID,
        assetType: depType,
        fileName: file?.name,
        source: base64,
        createSnackbar,
      });
      return { mongoId: null, base64: await optimize(depType, base64, file?.name) };
    },
    async uploadAssetPair(imageFile, metaFile, depType) {
      const [image, meta] = await Promise.all([
        localFileToBase64(imageFile),
        localFileToBase64(metaFile),
      ]);
      void keepUploadInLibrary({
        baseID,
        assetType: depType,
        fileName: imageFile?.name,
        source: { image, xml: meta },
        createSnackbar,
      });
      // Pairs are left alone by the optimizer (see snapshotOptimize) —
      // routed through it anyway so there is one gate, not two rules.
      const optimized = await optimize(depType, { image, meta }, imageFile?.name);
      return { image: optimized.image, meta };
    },
  };
}

// The panels read every collection off props; with no library they are all empty
// and the pickers they feed are hidden anyway.
const EMPTY_COLLECTIONS = {
  atlases: [],
  bitmapFonts: [],
  fonts: [],
  media: { images: [] },
  sounds: [],
  videos: [],
};

export default function ContainerSnapshotConfig({
  createSnackbar,
  editor,
  isAdmin,
  selectedBase,
  selectedGame,
  userPermissions,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const TABS = hasAdsManagerClaims(selectedBase)
    ? [...BASE_TABS.slice(0, -1), { key: "jobQueue", label: "Queue", icon: <AssignmentIcon /> }, BASE_TABS[BASE_TABS.length - 1]]
    : BASE_TABS;
  const [tabValue, setTabValue] = React.useState(0);

  // The agent tab is mounted the first time it is opened and kept mounted from
  // then on — see renderPanelBody for why it is late and why it stays.
  const [agentEverOpened, setAgentEverOpened] = useState(false);

  // Assets tab: scroll to + highlight a row when Version Settings links to an
  // asset key ("→ asset foo-123" under a component's dependency).
  const [highlightAssetKey, setHighlightAssetKey] = useState(null);

  const navigateToAsset = useCallback((assetKey) => {
    if (!assetKey) return;
    setHighlightAssetKey(assetKey);
    setTabValue(TABS.findIndex((tab) => tab.key === "assets"));
  }, []);

  /**
   * A version-shaped view of the snapshot. The panels read
   * `selectedVersion.playEngineConfig.*` and nothing else off it, so the file
   * name stands in for the id and title.
   *
   * Built from the LOADED file, not the selected one: the panels treat a new
   * `_id` as a version switch and throw away everything they know, so naming a
   * file whose config has not arrived yet would make them do that twice — once
   * against the previous config, once for real.
   */
  // Version Settings can pull an asset straight out of the scraped library, the
  // same one the Assets tab browses. The picker is a dialog, so the request is
  // held here with the promise the panel is awaiting; choosing (or closing)
  // settles it. `null` back means "cancelled" and the panel leaves things be.
  const [libraryRequest, setLibraryRequest] = useState(null);

  const pickFromLibrary = useCallback(
    async (assetType) => {
      if (!LIBRARY_ASSET_TYPES.includes(assetType)) return null;
      // Same snapshot-wide guess the Assets tab opens to — no bound asset to
      // ask a more specific folder of here, this picker only ever knows a
      // type, not which dependency it is filling.
      const initialDirPath = await guessSnapshotAssetsFolder(
        editor.baseID,
        editor.snapshot,
        editor.loadedFileName
      );
      return new Promise((resolve) => {
        setLibraryRequest({ assetType, initialDirPath, resolve });
      });
    },
    [editor.baseID, editor.snapshot, editor.loadedFileName]
  );

  const assetProvider = useMemo(
    () => ({ ...buildUploadProvider({ baseID: editor.baseID, createSnackbar }), pickFromLibrary }),
    [editor.baseID, createSnackbar, pickFromLibrary]
  );

  const closeLibrary = useCallback((source) => {
    setLibraryRequest((current) => {
      current?.resolve(source ?? null);
      return null;
    });
  }, []);

  const versionAdapter = useMemo(() => {
    if (!editor.loadedFileName || !editor.snapshot) return null;
    return {
      _id: editor.loadedFileName,
      title: editor.loadedFileName.replace(/\.json$/i, ""),
      playEngineConfig: editor.snapshot,
      dashboardMeta: _.get(editor.snapshot, "metadata.dashboardMeta", {}),
    };
  }, [editor.loadedFileName, editor.snapshot]);

  // Seeded from the snapshot, which remembers the language it was last edited
  // in; the panels report their own switches back through setLocalizationLanguage.
  const [localizationLanguage, setLocalizationLanguage] = useState("");

  useEffect(() => {
    const saved = _.get(versionAdapter, "playEngineConfig.localization.currentLanguage");
    setLocalizationLanguage(saved || "en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_.get(versionAdapter, "_id")]);

  /**
   * The panels' single write path, translated onto the snapshot file.
   *
   * `dashboardMeta` has no file of its own here, so it rides along inside the
   * snapshot's own metadata block — harmless to the engine and to the agent,
   * which both ignore unknown metadata keys.
   */
  const setSelectedVersionPathValue = useCallback(
    (p) => {
      if (!p || typeof p.path !== "string") return;

      if (p.path === "playEngineConfig") {
        editor.applySnapshotChange(p.value);
        return;
      }
      if (p.path.startsWith("playEngineConfig.")) {
        editor.setSnapshotPathValue(p.path.slice("playEngineConfig.".length), p.value);
        return;
      }
      if (p.path === "dashboardMeta" || p.path.startsWith("dashboardMeta.")) {
        editor.setSnapshotPathValue(
          p.path.replace(/^dashboardMeta/, "metadata.dashboardMeta"),
          p.value
        );
        return;
      }

      // Everything else is a mongo version field (title, isLocked, …) with no
      // meaning for a file-backed snapshot. Renaming happens in the tree.
      console.debug(`[snapshotEditor] ignoring version-only path "${p.path}"`);
    },
    [editor]
  );

  const sharedProps = {
    createSnackbar,
    iFrameId: SNAPSHOT_IFRAME_ID,
    isPreviewLoaded: true,
    // One language for the whole pane. Version Settings and Custom Variables
    // both edit the snapshot's `localization` block, and a panel showing a
    // different language than the preview would write its translations into the
    // wrong one — so the choice is held here and every panel reads it.
    localizationLanguage,
    setLocalizationLanguage,
    selectedBase,
    selectedGame,
    selectedVersion: versionAdapter,
    setSelectedVersionPathValue,
    // Undo/redo rewrites the snapshot underneath the panels, and they edit their
    // own copy of it rather than the snapshot itself. This counter is how they
    // hear about it: on every change they re-read what they show from the
    // restored config. Absent in the version editor, where nothing rewrites a
    // version behind the panels' back.
    externalConfigRevision: editor.configRevision,
    // A locked snapshot is presented exactly like a user without update rights:
    // the reused playEngine panel already guards every write behind
    // `userPermissions.editContent === false`, so the lock rides that same path
    // instead of a second read-only concept threaded through the same code.
    userPermissions: editor.isLocked ? { ...userPermissions, update: false } : userPermissions,
  };

  const versionSettingsProps = {
    ...sharedProps,
    ...EMPTY_COLLECTIONS,
    assetProvider,
    onNavigateToAsset: navigateToAsset,
    // A boolean, not a setter, despite the name — the panel reads it to label
    // and clamp its position editors.
    newSetPortrait: editor.isPortrait,
    // resolvePlayEngineConfig falls back to selectedVersion when the id is not
    // in this list, which is exactly what we want: there is no versions list.
    versions: [],
  };

  const activeKey = (TABS[tabValue] || TABS[0]).key;

  // One-way latch: the agent panel appears on the first visit and is never taken
  // down again for the rest of this editor's life.
  useEffect(() => {
    if (activeKey === "snapshotAgent") setAgentEverOpened(true);
  }, [activeKey]);

  /**
   * Once a snapshot is open, all four snapshot-backed panels stay mounted and
   * the inactive ones are merely hidden — the same thing the version editor does
   * with its SwipeableViews.
   *
   * This is not cosmetic: those panels build their component tree from the
   * engine and only ask for it when the iframe announces
   * `playengine:playable-ready`. A panel that is unmounted when that fires never
   * hears it, and one mounted afterwards has nothing left to trigger a fetch —
   * it just sits there reporting no components. Keeping them mounted keeps
   * their listeners alive across tab switches.
   *
   * Hidden no longer means re-rendered, though: `renderCachedPanel` below
   * hands React back the element it already has, so a panel off screen keeps
   * its listeners and its state without rebuilding its tree on every edit
   * made in another tab.
   *
   * They are mounted only WITH a snapshot, never before: those panels bail out
   * early on a nil `selectedVersion` and still declare hooks after that point,
   * so mounting one without a version and handing it one later changes the hook
   * count mid-life and React tears the tree down.
   */
  function renderPanelBody(key) {
    if (key === "jobQueue") return <JobQueuePanel baseID={editor.baseID} isActive={activeKey === key} canEditClaims={userPermissions?.editContent === true || userPermissions?.update === true} />;
    if (key === "snapshotAgent") {
      // Mounted late and then kept — both halves for reasons of its own.
      //
      // KEPT, but not for the reason the others are: the rest stay alive to
      // catch `playengine:playable-ready` from the iframe, which this one never
      // listens for. It stays because of what it holds — a WebSocket per open
      // conversation, the session each card is attached to, and whatever has
      // been typed into a composer but not sent yet. Unmounting it threw all
      // three away: a half-written prompt did not survive a look at another tab.
      //
      // What made unmounting attractive is real and is handled instead by
      // `isActive`: this panel re-renders a whole transcript on every message,
      // and a session streams one per step the agent takes, on the main thread.
      // While it is hidden the cards render nothing at all (see SessionCard), so
      // an arriving step costs a state write and no tree — the panels and the
      // preview iframe are left alone, which is what unmounting was buying.
      //
      // LATE, because mounting asks the agent container for its flows and the
      // sessions on this base. Someone who only edits settings should not pay
      // for that round trip, so nothing happens until the tab is opened once.
      if (!agentEverOpened) return null;
      return (
        <SnapshotAgent
          isActive={activeKey === "snapshotAgent"}
          selectedBase={selectedBase}
          isAdmin={isAdmin}
          createSnackbar={createSnackbar}
          onSnapshotsChanged={editor.refreshSnapshots}
        />
      );
    }

    if (!versionAdapter) return null;

    if (key === "toolbox") {
      // Never frozen (see renderCachedPanel): a button can be mid-run, waiting
      // on a pick list, while another tab is open. It handles a locked snapshot
      // itself — buttons that only read or reload the preview stay usable.
      return (
        <ContainerSnapshotToolbox
          editor={editor}
          userPermissions={userPermissions}
          createSnackbar={createSnackbar}
        />
      );
    }

    switch (key) {
    case "versionSettings":
      return <PlayEngineVersionSettings {...versionSettingsProps} />;
    case "assets":
      return (
        <ContainerAssets
          editor={editor}
          userPermissions={sharedProps.userPermissions}
          createSnackbar={createSnackbar}
          highlightAssetKey={highlightAssetKey}
          onHighlightDone={() => setHighlightAssetKey(null)}
          onNavigateToAsset={navigateToAsset}
        />
      );
    case "generalSettings":
      return <PlayEngineGeneralSettings {...sharedProps} />;
    case "customVariables":
      return <PlayEngineCustomVariables {...sharedProps} />;
    case "design":
      // Same orientation flag the version settings panel gets: responsive rows
      // edit the half the preview is showing. The provider and the jump are
      // what let an asset row replace a slot here rather than only point at it.
      return (
        <PlayEngineDesign
          {...sharedProps}
          newSetPortrait={editor.isPortrait}
          assetProvider={assetProvider}
          onNavigateToAsset={navigateToAsset}
          // Which design this snapshot resolves to is a property of the FILE,
          // not of the design, so the binding is written here where the
          // snapshot is owned. Flushed rather than debounced: the panel re-reads
          // the design from disk straight afterwards, and would otherwise read
          // the binding it is replacing.
          bindSnapshotDesign={async (designId) => {
            editor.setSnapshotPathValue("metadata.design", designId || null);
            await editor.flushSave();
          }}
        />
      );
    case "scripts":
      return <PlayEngineScripts {...sharedProps} />;
    case "sounds":
      // assetProvider is what tells this panel it has no media library here:
      // a snapshot embeds its audio, so uploads go through the provider and
      // the library dropdown is not offered.
      return <PlayEngineSounds {...sharedProps} assetProvider={assetProvider} />;
    default:
      return null;
    }
  }

  /**
   * Only the open tab is rendered; the rest are frozen, not unmounted.
   *
   * These are the version editor's panels, and they cost what they cost —
   * Version Settings rebuilds itself from the engine on mount. Handing React the
   * same element object back while a tab is off screen makes it bail out of the
   * subtree, so an edit in one panel stops re-rendering the other six. See the
   * matching comment in containerGameConfig.
   */
  const panelCacheRef = useRef(new Map());

  /**
   * The identity a cached panel body was built under — see `shouldRebuildPanel`.
   *
   * Two things here are not edits. Opening a different snapshot is one: Version
   * Settings drops its component tree and refills it from the engine's one-shot
   * `playengine:playable-ready`, which a panel still holding the old snapshot
   * lets pass. Undo/redo is the other — `externalConfigRevision` is the only
   * signal the panels get that the config was rewritten underneath them, and a
   * frozen panel would go on showing the values that were undone.
   */
  const panelGeneration = `${editor.loadedFileName || ""}|${editor.configRevision}`;

  // The agent panel is never frozen: it already skips rendering its transcript
  // while hidden (see its note in renderPanelBody above), and it needs a live
  // `isActive` to know that it is hidden in the first place.
  //
  // Version Settings is built as soon as there is a snapshot to build it from,
  // open tab or not: it is the one panel with nothing but the engine's one-shot
  // announcement to fetch on, and someone who left the rail on another tab
  // before loading a snapshot would otherwise open it to an empty tree. Not
  // before a snapshot exists, though — `renderPanelBody` returns null without
  // one, and mounting these panels versionless is the hazard noted above.
  function renderCachedPanel(key) {
    if (key === "snapshotAgent" || key === "jobQueue" || key === "toolbox") return renderPanelBody(key);
    const cache = panelCacheRef.current;
    const cached = cache.get(key) || null;

    if (
      shouldRebuildPanel({
        cached,
        generation: panelGeneration,
        isActive: activeKey === key,
        mountEagerly: key === "versionSettings" && Boolean(versionAdapter),
      })
    ) {
      cache.set(key, { generation: panelGeneration, body: renderPanelBody(key) });
    }

    const entry = cache.get(key);
    return entry ? entry.body : null;
  }

  return (
    <div className={classes.root}>
      {/* Blender-style vertical icon rail (left) + content (right) — the same
          chrome the version editor's playEngine panel uses. */}
      <div className={classes.body}>
        <div role="tablist" className={classes.rail}>
          {TABS.map((tab, index) => {
            const selected = tabValue === index;
            return (
              <Tooltip key={tab.key} title={tab.label} placement="right">
                <IconButton
                  role="tab"
                  aria-selected={selected}
                  aria-label={tab.label}
                  onClick={() => setTabValue(index)}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 0,
                    color: selected ? theme.palette.primary.main : "#bbb",
                    opacity: selected ? 1 : 0.7,
                    borderLeft: selected
                      ? `2px solid ${theme.palette.primary.main}`
                      : "2px solid transparent",
                    backgroundColor: selected ? "rgba(144, 202, 249, 0.12)" : "transparent",
                  }}
                >
                  {tab.icon}
                </IconButton>
              </Tooltip>
            );
          })}

          {/* Undo / redo for the edits made in these panels. They sit at the
              foot of the rail, away from the tabs, because they act on the
              snapshot rather than switching what you are looking at. History is
              in memory only and resets when another snapshot is opened. */}
          <div style={{ flex: 1 }} />
          <div className={classes.railFooter}>
            <Tooltip title="Undo the last change" placement="right">
              <span>
                <IconButton
                  aria-label="Undo"
                  onClick={editor.undo}
                  disabled={!editor.canUndo}
                  style={{ width: 44, height: 36, borderRadius: 0, color: "#bbb" }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo" placement="right">
              <span>
                <IconButton
                  aria-label="Redo"
                  onClick={editor.redo}
                  disabled={!editor.canRedo}
                  style={{ width: 44, height: 36, borderRadius: 0, color: "#bbb" }}
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className={classes.panel}>
          {/* The snapshot-backed panels render nothing without a version, so say
              why the pane is empty rather than leaving it blank. */}
          {!versionAdapter && activeKey !== "snapshotAgent" && activeKey !== "jobQueue" && (
            <Typography className={classes.placeholder}>
              {editor.selectedFileName
                ? "Loading snapshot…"
                : "Select a snapshot on the left to edit it."}
            </Typography>
          )}

          {/* A locked snapshot takes no edits. The panels already refuse every
              write (they read the same `userPermissions.editContent === false` a
              user without update rights gets); this says so once, greys the
              editors out and makes them inert so a click cannot reach them at
              all. Version Settings keeps its outliner live — see
              `lockedPropsOnly`. */}
          {editor.isLocked && versionAdapter && activeKey !== "snapshotAgent" && activeKey !== "jobQueue" && (
            <div className={classes.lockedBanner}>
              <LockIcon style={{ fontSize: "15px" }} />
              <span>
                This snapshot is locked — unlock it from the tree on the left to make changes.
              </span>
            </div>
          )}

          {TABS.map((tab) => {
            const isLockedPanel =
              editor.isLocked &&
              Boolean(versionAdapter) &&
              tab.key !== "snapshotAgent" &&
              tab.key !== "jobQueue" &&
              tab.key !== "toolbox";
            const lockedClass = tab.key === "versionSettings" ? classes.lockedPropsOnly : classes.lockedPanel;
            return (
              <div
                key={tab.key}
                role="tabpanel"
                hidden={activeKey !== tab.key}
                aria-readonly={isLockedPanel || undefined}
                className={isLockedPanel ? lockedClass : undefined}
                style={{ height: "100%" }}
              >
                {renderCachedPanel(tab.key)}
              </div>
            );
          })}
        </div>
      </div>

      {libraryRequest && (
        <DialogAssetLibrary
          baseID={editor.baseID}
          assetType={libraryRequest.assetType}
          multiple={false}
          initialDirPath={libraryRequest.initialDirPath}
          // The library is shared, not the snapshot — but a user who cannot
          // edit here has no business filling it either. A locked snapshot is
          // the same signal (see `sharedProps.userPermissions`).
          canWrite={sharedProps.userPermissions?.update !== false}
          createSnackbar={createSnackbar}
          onClose={() => closeLibrary(null)}
          onChoose={async (entries) => {
            try {
              const source = await sourceFromLibraryEntry(entries?.[0]);
              closeLibrary(
                await optimizeSourceForSnapshot({
                  baseID: editor.baseID,
                  assetType: libraryRequest.assetType,
                  source,
                  fileName: entries?.[0]?.name,
                  onSaved: (stats) =>
                    createSnackbar?.(
                      `Image optimized by ${stats.savedPercent}% for this snapshot.`,
                      "success",
                      false
                    ),
                })
              );
            } catch (err) {
              createSnackbar?.(`Could not read that asset: ${err.message}`, "error", false);
              closeLibrary(null);
            }
          }}
        />
      )}
    </div>
  );
}
